/**
 * Shopping Preferences Storage Utilities
 *
 * Cookie-based storage for shopping preferences.
 * Supports both permanent and temporary storage types.
 */

import Cookies from 'universal-cookie';
import { ShoppingPreferences, StorageType } from '@lib/shopping-preferences/types';
import { AppCookies } from '@enums/cookies';
import logger from '@helpers/utils/logger/client';

const cookies = new Cookies();

export const COOKIE_KEY_PERMANENT = AppCookies.USER_SHOPPING_PREFERENCES;
export const COOKIE_KEY_TEMPORARY = 'user_shopping_preferences_session';

/**
 * Cookie options for permanent storage (1 year)
 */
const getPermanentCookieOptions = () => ({
  path: '/',
  maxAge: 60 * 60 * 24 * 365, // 1 year
  sameSite: 'lax' as const,
  secure: process.env.NODE_ENV === 'production',
});

/**
 * Cookie options for temporary storage (session only)
 */
const getTemporaryCookieOptions = () => ({
  path: '/',
  // No maxAge = session cookie (deleted when browser closes)
  sameSite: 'lax' as const,
  secure: process.env.NODE_ENV === 'production',
});

/**
 * Safely parse JSON with error handling
 */
const safeParse = <T>(value: unknown): T | null => {
  // If it's already an object, return it
  if (!value || typeof value === 'object' || typeof value !== 'string') {
    return value as T | null;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    logger.error('Failed to parse preferences from cookie:', error);
    return null;
  }
};

/**
 * Storage operations for shopping preferences cookies
 */
export const storage = {
  /**
   * Get preferences from cookie
   * @param storageType - Optional filter by storage type
   */
  get: (storageType?: StorageType): ShoppingPreferences | null => {
    try {
      if (storageType) {
        const cookieKey = storageType === 'temporary' ? COOKIE_KEY_TEMPORARY : COOKIE_KEY_PERMANENT;
        const cookieValue = cookies.get(cookieKey);
        if (!cookieValue) {
          return null;
        }
        return safeParse<ShoppingPreferences>(cookieValue);
      }

      // No filter - check temporary first (higher priority), then permanent
      const tempValue = cookies.get(COOKIE_KEY_TEMPORARY);
      if (tempValue) {
        return safeParse<ShoppingPreferences>(tempValue);
      }

      const permValue = cookies.get(COOKIE_KEY_PERMANENT);
      if (permValue) {
        return safeParse<ShoppingPreferences>(permValue);
      }

      return null;
    } catch (error) {
      logger.error('Error reading shopping preferences cookie:', error);
      return null;
    }
  },

  /**
   * Get temporary (session) preferences
   */
  getTemporary: (): ShoppingPreferences | null => {
    return storage.get('temporary');
  },

  /**
   * Get permanent preferences
   */
  getPermanent: (): ShoppingPreferences | null => {
    return storage.get('permanent');
  },

  /**
   * Set preferences in cookie
   * @param prefs - Preferences to store
   * @param storageType - Whether to store permanently or temporarily
   */
  set: (prefs: ShoppingPreferences, storageType: StorageType = 'permanent'): boolean => {
    try {
      const prefsWithType: ShoppingPreferences = {
        ...prefs,
        storage_type: storageType,
      };

      const cookieKey = storageType === 'temporary' ? COOKIE_KEY_TEMPORARY : COOKIE_KEY_PERMANENT;
      const options = storageType === 'permanent' ? getPermanentCookieOptions() : getTemporaryCookieOptions();

      cookies.set(cookieKey, prefsWithType, options);
      // When the user set permanent prefs, those are prioritzed, then remove temporary
      if (storageType === 'permanent') {
        cookies.remove(COOKIE_KEY_TEMPORARY, { path: '/' });
      }
      logger.info(`[Storage] Set ${storageType} preferences in cookie`);
      return true;
    } catch (error) {
      logger.error('Error setting shopping preferences cookie:', error);
      return false;
    }
  },

  /**
   * Set temporary (session) preferences
   */
  setTemporary: (prefs: ShoppingPreferences): boolean => {
    return storage.set(prefs, 'temporary');
  },

  /**
   * Set permanent preferences
   */
  setPermanent: (prefs: ShoppingPreferences): boolean => {
    return storage.set(prefs, 'permanent');
  },

  /**
   * Remove preferences cookie(s)
   * @param storageType - Optional: which cookie to remove
   */
  remove: (storageType?: StorageType): boolean => {
    try {
      if (!storageType) {
        // Remove both
        cookies.remove(COOKIE_KEY_PERMANENT, { path: '/' });
        cookies.remove(COOKIE_KEY_TEMPORARY, { path: '/' });
        logger.info('[Storage] Removed all preferences cookies');
      } else {
        const cookieKey = storageType === 'temporary' ? COOKIE_KEY_TEMPORARY : COOKIE_KEY_PERMANENT;
        cookies.remove(cookieKey, { path: '/' });
        logger.info(`[Storage] Removed ${storageType} preferences cookie`);
      }
      return true;
    } catch (error) {
      logger.error('Error removing shopping preferences cookie:', error);
      return false;
    }
  },

  /**
   * Check if preferences exist in cookie
   * @param storageType - Optional filter by storage type
   */
  has: (storageType?: StorageType): boolean => {
    return storage.get(storageType) !== null;
  },

  /**
   * Check if temporary preferences exist
   */
  hasTemporary: (): boolean => {
    return storage.has('temporary');
  },

  /**
   * Check if permanent preferences exist
   */
  hasPermanent: (): boolean => {
    return storage.has('permanent');
  },

  /**
   * Clear specific storage type or all
   * @param type - 'temporary', 'permanent', or 'all'
   */
  clear: (type: 'temporary' | 'permanent' | 'all' = 'all'): boolean => {
    if (type === 'all') {
      return storage.remove();
    }

    return storage.remove(type);
  },
};

// Export legacy key names for backward compatibility
export const LOCAL_KEY = 'shoppingPrefs'; // deprecated
export const SESSION_KEY = 'shoppingPrefsSession'; // deprecated

// Export convenience aliases
export const cookieStorage = storage;
